import { hubPageBg, hubLinkColor, hubGhostBtnSx } from './accountHubStyles';
import { isAccountHubEventForUser } from './accountHubUtils';
import { useAsUserParam } from './useAsUserParam';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import ProxyBanner from './ProxyBanner';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import axios from 'src/utils/axios';
import UlcmContactService from 'src/services/UlcmContact.service';
import OtpCodeInput from 'src/components/shared/OtpCodeInput';
import { formatPhoneNumber as formatPhoneDisplay } from 'src/utils/formatNumber';
import type { UlcmPendingProposal } from 'src/services/UlcmContact.service';

interface LoginSecurityData {
  email: string;
  primaryEmailId: number | null;
  totp_enabled: boolean;
  totp_confirmed_at: string | null;
  backup_codes_remaining: number;
}

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
  p: 2,
};

const LoginSecurityPage: FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const asUser = useAsUserParam();
  const [data, setData] = useState<LoginSecurityData | null>(null);
  const [loading, setLoading] = useState(true);

  // Pending proposals
  const [pendingProposals, setPendingProposals] = useState<
    UlcmPendingProposal[]
  >([]);
  const [confirmingOtpId, setConfirmingOtpId] = useState<string | null>(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmSaving, setConfirmSaving] = useState(false);

  // Keep a stable ref to userId for the WS handler closure
  const userIdRef = useRef<number | null>(userId ? Number(userId) : null);
  useEffect(() => {
    userIdRef.current = userId ? Number(userId) : null;
  }, [userId]);

  // Inline email edit
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Inline password change
  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );

  const isSelf = user?.id === Number(userId);

  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${
    userId ?? ''
  }`;
  const securityRoute = `${backRoute}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}`;

  useEffect(() => {
    const loadSecurityData = async (): Promise<void> => {
      try {
        const [totpResp, contactsResp] = await Promise.all([
          axios.get(`v2/user/totp/enroll/${asUser}`).catch(() => null),
          axios.get(`v2/user/contacts/${asUser}`).catch(() => null),
        ]);
        const primaryEmailEntry = contactsResp?.data?.emails?.find(
          (e: { is_primary?: boolean; email?: string; id?: number }) =>
            e.is_primary
        );
        const email =
          primaryEmailEntry?.email ??
          contactsResp?.data?.login_email ??
          user?.email ??
          '';
        setData({
          email,
          primaryEmailId: primaryEmailEntry?.id ?? null,
          totp_enabled: Boolean(totpResp?.data?.confirmed),
          totp_confirmed_at: totpResp?.data?.confirmed_at ?? null,
          backup_codes_remaining: totpResp?.data?.backup_codes_remaining ?? 0,
        });
        setPendingProposals(contactsResp?.data?.pending_proposals ?? []);
      } catch {
        setData({
          email: user?.email ?? '',
          primaryEmailId: null,
          totp_enabled: false,
          totp_confirmed_at: null,
          backup_codes_remaining: 0,
        });
      } finally {
        setLoading(false);
      }
    };
    void loadSecurityData();
  }, [user, asUser]);

  // WS-driven pending proposal updates
  useEffect(() => {
    const handleWsEvent = (event: CustomEvent): void => {
      if (!event.detail || typeof event.detail.type !== 'string') {
        return;
      }
      const { type, data: wsData } = event.detail as {
        type: string;
        data: unknown;
      };

      if (!isAccountHubEventForUser(wsData, userIdRef.current)) {
        return;
      }

      if (
        type === 'contact_info_pending_change' &&
        wsData &&
        typeof wsData === 'object'
      ) {
        const proposal = wsData as {
          otp_id: string;
          contact_type?: string;
          contact_value?: string;
        };
        setPendingProposals((prev) => {
          if (prev.some((p) => p.otp_id === proposal.otp_id)) {
            return prev;
          }
          return [
            ...prev,
            {
              otp_id: proposal.otp_id,
              contact_type: (proposal.contact_type ?? 'email') as
                | 'email'
                | 'phone',
              contact_value: proposal.contact_value ?? '',
            },
          ];
        });
      } else if (
        type === 'contact_info_pending_cancelled' &&
        wsData &&
        typeof wsData === 'object'
      ) {
        const { change_id } = wsData as { change_id?: string };
        if (change_id) {
          setPendingProposals((prev) =>
            prev.filter((p) => p.otp_id !== change_id)
          );
        }
      } else if (type === 'contact_info_updated') {
        // Full refresh — clear all pending proposals (they've been resolved)
        setPendingProposals([]);
      }
    };

    document.addEventListener(
      'UserWebSocketEvent',
      handleWsEvent as EventListener
    );
    return () => {
      document.removeEventListener(
        'UserWebSocketEvent',
        handleWsEvent as EventListener
      );
    };
  }, []);

  // ── Accept / Reject pending proposals ────────────────────────────────────
  const handleRejectProposal = useCallback(
    async (otpId: string): Promise<void> => {
      try {
        await UlcmContactService.cancelOwnProposal(otpId, undefined, asUser);
      } catch {
        // If already cancelled (404), treat as done
      }
    },
    [asUser]
  );

  const handleConfirmOtp = useCallback(
    async (_otpId: string): Promise<void> => {
      if (confirmCode.length < 6) {
        return;
      }
      setConfirmSaving(true);
      setConfirmError(null);
      try {
        const result = await UlcmContactService.confirmOtp(
          'contact_change',
          confirmCode,
          asUser
        );
        if (result.confirmed) {
          setConfirmingOtpId(null);
          setConfirmCode('');
          // Refresh contacts to pick up newly-verified email
          try {
            const latest = await UlcmContactService.listContacts(asUser);
            const newEmail =
              latest.emails.find((e) => e.is_primary)?.email ??
              data?.email ??
              '';
            setData((prev) => (prev ? { ...prev, email: newEmail } : prev));
            setPendingProposals(latest.pending_proposals);
          } catch {
            // Non-fatal — WS will reconcile
          }
        } else if (result.is_locked) {
          setConfirmError(
            'Too many failed attempts. This proposal has been locked.'
          );
        } else {
          setConfirmError(
            `Invalid code. ${result.attempts_remaining} attempt(s) remaining.`
          );
        }
      } catch {
        setConfirmError('Verification failed. Please try again.');
      } finally {
        setConfirmSaving(false);
      }
    },
    [confirmCode, asUser, data?.email]
  );

  const handleStartEditEmail = useCallback((): void => {
    setEmailDraft(data?.email ?? '');
    setEmailError(null);
    setEditingEmail(true);
  }, [data?.email]);

  const handleCancelEmail = useCallback((): void => {
    setEditingEmail(false);
    setEmailDraft('');
    setEmailError(null);
  }, []);

  const handleSaveEmail = useCallback(async (): Promise<void> => {
    if (!emailDraft.trim() || emailDraft === data?.email) {
      setEditingEmail(false);
      return;
    }
    setEmailSaving(true);
    setEmailError(null);
    try {
      const result = await UlcmContactService.proposeEmail(
        emailDraft.trim(),
        undefined,
        asUser,
        data?.primaryEmailId ?? null
      );
      // Close the text field and immediately open OTP inline — inject a local
      // pending proposal so the confirmation card renders without waiting for WS.
      setEditingEmail(false);
      setPendingProposals((prev) => {
        if (prev.some((p) => p.otp_id === result.otp_id)) {
          return prev;
        }
        return [
          ...prev,
          {
            otp_id: result.otp_id,
            contact_type: 'email' as const,
            contact_value: emailDraft.trim(),
            replace_id: data?.primaryEmailId ?? null,
          },
        ];
      });
      setConfirmingOtpId(result.otp_id);
      setConfirmCode('');
      setConfirmError(null);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string; detail?: string } } })
          ?.response?.data?.error ??
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        'Could not update email. Please try again.';
      setEmailError(msg);
    } finally {
      setEmailSaving(false);
    }
  }, [emailDraft, data?.email, data?.primaryEmailId, asUser]);

  const handleStartEditPassword = useCallback((): void => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setEditingPassword(true);
  }, []);

  const handleCancelPassword = useCallback((): void => {
    setEditingPassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(false);
  }, []);

  const handleSavePassword = useCallback(async (): Promise<void> => {
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    setPasswordSaving(true);
    setPasswordError(null);
    try {
      await axios.post(`v2/user/password/change/${asUser}`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setEditingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess(true);
      // Auto-dismiss after 5 seconds
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string; error?: string } } })
          ?.response?.data?.detail ??
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ??
        'Could not change password. Check your current password and try again.';
      setPasswordError(msg);
    } finally {
      setPasswordSaving(false);
    }
  }, [currentPassword, newPassword, confirmPassword, asUser]);

  const handleManage2FA = useCallback((): void => {
    navigate(`${securityRoute}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}`);
  }, [navigate, securityRoute]);

  const handleViewSessions = useCallback((): void => {
    navigate(
      `${securityRoute}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_SESSIONS}`
    );
  }, [navigate, securityRoute]);

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Login & Security"
        icon={<LockIcon />}
        backRoute={backRoute}
        subtitle="Manage your password and authentication"
        hideBackOnDesktop
      />
      <ProxyBanner />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack gap={2.5} sx={{ p: 3 }}>
          {/* ── Pending Confirmation panel ── */}
          {pendingProposals.length > 0 && (
            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
                Pending Confirmation
              </Typography>
              {pendingProposals.map((p) => (
                <Stack key={p.otp_id} gap={1} sx={{ mb: 1.5 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Typography
                      sx={{ fontSize: 13, color: 'text.secondary' }}
                      noWrap
                    >
                      {p.contact_type === 'phone'
                        ? formatPhoneDisplay(p.contact_value)
                        : p.contact_value}
                    </Typography>
                    <Stack direction="row" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          fontSize: 12,
                          borderRadius: '8px',
                          backgroundColor: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.dark' },
                        }}
                        onClick={() => {
                          setConfirmingOtpId(p.otp_id);
                          setConfirmCode('');
                          setConfirmError(null);
                        }}
                      >
                        Accept
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: 12,
                          borderRadius: '8px',
                          borderColor: 'error.main',
                          color: 'error.main',
                        }}
                        onClick={() => void handleRejectProposal(p.otp_id)}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </Stack>

                  {confirmingOtpId === p.otp_id && (
                    <Stack gap={1.5} sx={{ pt: 0.5 }}>
                      <Typography
                        sx={{ fontSize: 12, color: 'text.secondary' }}
                      >
                        Enter the verification code sent to confirm ownership.
                      </Typography>
                      {confirmError && (
                        <Alert severity="error" sx={{ py: 0 }}>
                          {confirmError}
                        </Alert>
                      )}
                      <OtpCodeInput
                        value={confirmCode}
                        onChange={setConfirmCode}
                        disabled={confirmSaving}
                        error={Boolean(confirmError)}
                      />
                      <Stack direction="row" gap={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          onClick={() => {
                            setConfirmingOtpId(null);
                            setConfirmCode('');
                            setConfirmError(null);
                          }}
                          disabled={confirmSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={confirmCode.length < 6 || confirmSaving}
                          onClick={() => void handleConfirmOtp(p.otp_id)}
                          startIcon={
                            confirmSaving ? (
                              <CircularProgress size={14} />
                            ) : null
                          }
                        >
                          <span>Verify</span>
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              ))}
            </Box>
          )}

          <Box sx={cardSx}>
            <Stack gap={2}>
              {/* ── Email row ── */}
              <Stack gap={1}>
                {/* Label + Change button on same line */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                    Email
                  </Typography>
                  <Button
                    variant="contained"
                    disableElevation
                    disabled={editingEmail}
                    sx={hubGhostBtnSx}
                    onClick={handleStartEditEmail}
                  >
                    Change
                  </Button>
                </Stack>

                {editingEmail ? (
                  <Stack gap={1}>
                    {emailError && (
                      <Alert severity="error" sx={{ py: 0 }}>
                        {emailError}
                      </Alert>
                    )}
                    {/* Standard variant gives the underline-only look without a visible box */}
                    <TextField
                      value={emailDraft}
                      onChange={(e) => setEmailDraft(e.target.value)}
                      type="email"
                      variant="standard"
                      autoFocus
                      fullWidth
                      inputProps={{ style: { fontSize: 14, fontWeight: 500 } }}
                    />
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        onClick={handleCancelEmail}
                        disabled={emailSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => void handleSaveEmail()}
                        disabled={emailSaving || !emailDraft.trim()}
                      >
                        Save
                      </Button>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      Changing your login email will also update your primary
                      contact email.
                    </Typography>
                  </Stack>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'text.primary',
                        wordBreak: 'break-all',
                      }}
                    >
                      {data?.email}
                    </Typography>
                  </>
                )}
              </Stack>

              <Divider />

              {/* ── Password row ── */}
              <Stack gap={1}>
                {/* Label + Change password button on same line */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                    Password
                  </Typography>
                  {!editingPassword && (
                    <Button
                      variant="contained"
                      disableElevation
                      sx={hubGhostBtnSx}
                      onClick={handleStartEditPassword}
                    >
                      Change password
                    </Button>
                  )}
                </Stack>

                {editingPassword ? (
                  <Stack gap={1.5}>
                    {passwordError && (
                      <Alert severity="error" sx={{ py: 0 }}>
                        {passwordError}
                      </Alert>
                    )}
                    <TextField
                      label="Current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      type="password"
                      size="small"
                      fullWidth
                      autoFocus
                    />
                    <TextField
                      label="New password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      type="password"
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      type="password"
                      size="small"
                      fullWidth
                      error={
                        confirmPassword.length > 0 &&
                        newPassword !== confirmPassword
                      }
                      helperText={
                        confirmPassword.length > 0 &&
                        newPassword !== confirmPassword
                          ? 'Passwords do not match'
                          : undefined
                      }
                    />
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        onClick={handleCancelPassword}
                        disabled={passwordSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => void handleSavePassword()}
                        disabled={
                          passwordSaving ||
                          !currentPassword ||
                          !newPassword ||
                          !confirmPassword ||
                          newPassword !== confirmPassword
                        }
                      >
                        {passwordSaving ? 'Saving…' : 'Save'}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <>
                    {passwordSuccess && (
                      <Alert
                        severity="success"
                        sx={{ py: 0, mb: 0.5 }}
                        onClose={() => setPasswordSuccess(false)}
                      >
                        Password updated successfully.
                      </Alert>
                    )}
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'text.primary',
                        letterSpacing: 2,
                      }}
                    >
                      ••••••••
                    </Typography>
                  </>
                )}
              </Stack>

              <Divider />

              {/* ── 2FA row — only shown for the account owner ── */}
              {isSelf && (
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack gap={0.5}>
                      <Typography
                        sx={{ fontSize: 14, color: 'text.secondary' }}
                      >
                        Two-Factor Authentication
                      </Typography>
                      {data?.totp_enabled ? (
                        <Chip
                          label="Enabled"
                          size="small"
                          sx={{
                            backgroundColor: 'primary.lighter',
                            color: 'primary.dark',
                            fontWeight: 500,
                            fontSize: 12,
                            borderRadius: '12px',
                            height: 'auto',
                            width: 'fit-content',
                            '& .MuiChip-label': { px: 1, py: 0.5 },
                          }}
                        />
                      ) : (
                        <Chip
                          label="Not enabled"
                          size="small"
                          sx={{
                            backgroundColor: 'error.lighter',
                            color: 'error.dark',
                            fontWeight: 600,
                            fontSize: 12,
                            borderRadius: '4px',
                            height: 'auto',
                            width: 'fit-content',
                            '& .MuiChip-label': { px: 1, py: 0.5 },
                          }}
                        />
                      )}
                    </Stack>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleManage2FA}
                      sx={hubGhostBtnSx}
                    >
                      Manage
                    </Button>
                  </Stack>

                  <Divider />
                </>
              )}

              {/* ── Sessions link ── */}
              <Typography
                onClick={handleViewSessions}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: hubLinkColor,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                View active sessions →
              </Typography>
            </Stack>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default LoginSecurityPage;
